import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { labAPI, queueAPI, patientAPI } from '../../utils/api';
import PatientLookup from '../../components/shared/PatientLookup';
import { printLabOrderSlip, printLabResultReport } from '../../utils/printSlip';

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

const CATEGORIES = ['Haematology', 'Biochemistry', 'Serology', 'Urine', 'Stool', 'Hormones', 'Microbiology', 'Cardiac'];

const SOURCE_OPTIONS = ['Walking','OPD','ER / Emergency','IPD / Admitted','Ward','ICU','CCU','NICU','PICU','Operation Theater','Labour Room','Lab Counter','Home Visit','Referral','Other'];

function SearchableSelect({ value, onChange, options, placeholder = 'Search...' }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = options.filter(o => o.toLowerCase().includes(q.toLowerCase()));

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 7, background: 'white', overflow: 'hidden' }}>
                <input
                    value={open ? q : value}
                    onChange={e => { setQ(e.target.value); setOpen(true); }}
                    onFocus={() => { setQ(''); setOpen(true); }}
                    placeholder={value || placeholder}
                    style={{ flex: 1, padding: '9px 12px', border: 'none', outline: 'none', fontSize: 13 }}
                />
                <span onClick={() => setOpen(o => !o)} style={{ padding: '0 10px', cursor: 'pointer', color: '#64748b', fontSize: 11 }}>▼</span>
            </div>
            {open && filtered.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #d1d5db', borderRadius: 7, zIndex: 999, maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: 2 }}>
                    {filtered.map(opt => (
                        <div key={opt} onMouseDown={() => { onChange(opt); setQ(''); setOpen(false); }}
                            style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, background: opt === value ? '#f5f3ff' : 'white', color: opt === value ? '#7c3aed' : '#111', fontWeight: opt === value ? 700 : 400 }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                            onMouseLeave={e => e.currentTarget.style.background = opt === value ? '#f5f3ff' : 'white'}>
                            {opt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function LabNewOrder() {
    const location = useLocation();
    const [patient, setPatient] = useState(null);
    const [allTests, setAllTests] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [selectedTests, setSelectedTests] = useState([]);
    const [openCategory, setOpenCategory] = useState(null);
    const [form, setForm] = useState({ source: 'OPD', priority: 'Routine', clinical_info: '' });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(null);
    const [lastOrder, setLastOrder] = useState(null);
    const [searchQ, setSearchQ] = useState('');
    const [discount, setDiscount] = useState('');
    const [testTab, setTestTab] = useState('profiles');
    const patientLookupRef = useRef(null);
    const [patientOrders, setPatientOrders] = useState([]);
    const [printOrderNo, setPrintOrderNo] = useState('');
    const [printLoading, setPrintLoading] = useState(false);

    const printByOrderNo = async () => {
        const orderNo = printOrderNo.trim().toUpperCase();
        if (!orderNo) return toast.error('Order number darain');
        setPrintLoading(true);
        try {
            const detail = await labAPI.getOrderItems(orderNo);
            const { order: ord, items } = detail.data;
            if (!ord) { toast.error('Order not found: ' + orderNo); return; }
            const completedItems = items.filter(i => i.result_value);
            if (completedItems.length === 0) { toast.warning('No completed results found'); return; }

            // Fetch test parameters for all tests
            const uniqueTestIds = [...new Set(completedItems.map(i => i.test_id).filter(Boolean))];
            const paramsMap = {};
            await Promise.all(uniqueTestIds.map(async (tid) => {
                try { const pr = await labAPI.getTestParameters(tid); if (pr.data?.length) paramsMap[tid] = pr.data; } catch {}
            }));

            const gender = ord.gender;
            const getRange = (param, g) => {
                if (!param) return '';
                if (g === 'Male' && param.range_low_male != null) return `${param.range_low_male}–${param.range_high_male}`;
                if (g === 'Female' && param.range_low_female != null) return `${param.range_low_female}–${param.range_high_female}`;
                if (param.range_low != null && param.range_high != null) return `${param.range_low}–${param.range_high}`;
                return '';
            };

            const resultsData = [];
            completedItems.forEach(i => {
                const params = paramsMap[i.test_id] || [];
                resultsData.push({ _header: true, test_name: i.test_name, specimen_type: i.specimen_type });
                if (params.length > 0 && i.result_value && i.result_value.includes(':')) {
                    const parts = i.result_value.split(' | ');
                    parts.forEach(part => {
                        const colonIdx = part.indexOf(':');
                        if (colonIdx === -1) return;
                        const pName = part.substring(0, colonIdx).trim();
                        const pVal = part.substring(colonIdx + 1).trim();
                        const param = params.find(p => p.parameter_name === pName);
                        const rangeStr = getRange(param, gender);
                        resultsData.push({
                            _subparam: true, test_name: pName,
                            result_value: pVal, result_unit: param?.unit || i.result_unit || '',
                            normal_range: rangeStr || i.normal_range || '',
                            result_flag: i.result_flag || 'Normal',
                        });
                    });
                } else {
                    resultsData.push({
                        test_name: i.test_name, result_value: i.result_value,
                        result_unit: i.result_unit || '', normal_range: i.normal_range || i.default_range || '',
                        result_flag: i.result_flag || 'Normal',
                    });
                }
            });

            const patient = { full_name: ord.full_name, mrn: ord.mrn, gender: ord.gender, age_years: ord.age_years, phone: ord.phone };
            printLabResultReport(patient, ord.order_number, ord, resultsData);
            setPrintOrderNo('');
        } catch (e) { toast.error('Failed to fetch order'); }
        finally { setPrintLoading(false); }
    };

    useEffect(() => {
        labAPI.getTests('').then(r => setAllTests(r.data)).catch(() => setAllTests([]));
        labAPI.getProfiles().then(r => setProfiles(r.data)).catch(() => setProfiles([]));
        if (location.state?.patient_id) {
            patientAPI.getById(location.state.patient_id).then(r => {
                setPatient(r.data);
                setForm(f => ({ ...f, source: 'IPD / Admitted' }));
            }).catch(() => {});
        }
    }, []);

    useEffect(() => {
        if (!patient) { setPatientOrders([]); return; }
        labAPI.getPatientHistory(patient.id).then(r => {
            // Group items by order_number
            const orderMap = {};
            r.data.forEach(item => {
                if (!orderMap[item.order_number]) {
                    orderMap[item.order_number] = { order_number: item.order_number, order_time: item.order_time, priority: item.priority, status: item.status, items: [] };
                }
                orderMap[item.order_number].items.push(item);
            });
            setPatientOrders(Object.values(orderMap).slice(0, 10));
        }).catch(() => setPatientOrders([]));
    }, [patient]);

    const toggleTest = (id) => setSelectedTests(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
    const toggleCategory = (cat) => setOpenCategory(prev => prev === cat ? null : cat);

    const selectedTestObjects = allTests.filter(t => selectedTests.includes(t.id));
    const subtotal = selectedTestObjects.reduce((sum, t) => sum + (parseFloat(t.price) || 0), 0);
    const discountAmt = subtotal * (parseFloat(discount) || 0) / 100;
    const netTotal = subtotal - discountAmt;

    const searchResults = searchQ.trim().length > 1
        ? allTests.filter(t =>
            t.test_name.toLowerCase().includes(searchQ.toLowerCase()) ||
            t.test_code.toLowerCase().includes(searchQ.toLowerCase())
          )
        : [];

    const createOrder = async (confirmedPatient) => {
        if (!selectedTests.length) return toast.error('Please select at least one test');
        setLoading(true);
        try {
            const res = await labAPI.createOrder({ ...form, patient_id: confirmedPatient.id, tests: selectedTests, discount_percent: parseFloat(discount) || 0, net_total: netTotal });
            toast.success(`Lab order created: ${res.data.order_number}`);
            let tokenNumber = null;
            try {
                const tRes = await queueAPI.issueToken({ department: 'LAB', patient_name: confirmedPatient.full_name, patient_id: confirmedPatient.id });
                tokenNumber = tRes.data.token.token_number;
                toast.info(`Queue Token: ${tokenNumber}`);
            } catch {}
            printLabOrderSlip(confirmedPatient, res.data.order_number, selectedTestObjects, form, tokenNumber, parseFloat(discount) || 0);
            setLastOrder({ patient: confirmedPatient, orderNumber: res.data.order_number, tests: selectedTestObjects, form: { ...form } });
            setDone({ patient: confirmedPatient, orderNumber: res.data.order_number });
            setSelectedTests([]);
            setDiscount('');
            setPatient(null);
            setTimeout(() => { setDone(null); patientLookupRef.current?.reset?.(); }, 100);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create order');
        } finally { setLoading(false); }
    };

    const handleSubmit = async (e) => { e.preventDefault(); };

    return (
        <div style={{ maxWidth: 900 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{ fontSize: 32 }}>🔬</span>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', margin: 0 }}>Lab Order</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Laboratory — New Test Order</p>
                </div>
            </div>

            {/* Quick Print by Order Number */}
            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>🖨️</span>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>Print Report:</div>
                <input
                    placeholder="LAB-20260714-5683"
                    value={printOrderNo}
                    onChange={e => setPrintOrderNo(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && printByOrderNo()}
                    style={{ flex: 1, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }}
                />
                <button onClick={printByOrderNo} disabled={printLoading}
                    style={{ padding: '7px 18px', background: printLoading ? '#94a3b8' : '#0369a1', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {printLoading ? '...' : '🖨️ Print'}
                </button>
            </div>

            {done && (
                <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                    <p style={{ color: '#4c1d95', fontWeight: 700, margin: 0 }}>✅ Lab Order Created!</p>
                    <p style={{ color: '#4c1d95', fontSize: 14, margin: '4px 0 0' }}>
                        Order: <strong>{done.orderNumber}</strong> | {done.patient.full_name} ({done.patient.mrn})
                    </p>
                </div>
            )}

            {/* Patient Previous Lab Orders — Reprint Section */}
            {patient && patientOrders.length > 0 && (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 16, marginBottom: 16 }}>
                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', marginBottom: 12, margin: '0 0 12px' }}>🖨️ Reprint Lab Reports — {patient.full_name}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {patientOrders.map(ord => {
                            const completedItems = ord.items.filter(i => i.result_value);
                            const total = ord.items.length;
                            const ready = completedItems.length;
                            const dt = ord.order_time ? new Date(ord.order_time).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                            return (
                                <div key={ord.order_number} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                    <div>
                                        <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>{ord.order_number}</span>
                                        <span style={{ fontSize: 11, color: '#64748b', marginLeft: 10 }}>{dt}</span>
                                        <span style={{ fontSize: 11, color: ready === total ? '#16a34a' : '#d97706', marginLeft: 10, fontWeight: 600 }}>
                                            {ready}/{total} ready
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (ready === 0) { toast.warning('No completed results for this order'); return; }
                                            printLabResultReport(
                                                { full_name: patient.full_name, mrn: patient.mrn, gender: patient.gender, age_years: patient.age_years, phone: patient.phone },
                                                ord.order_number,
                                                { priority: ord.priority, source: '', order_time: ord.order_time },
                                                completedItems.map(i => ({ test_name: i.test_name, test_code: i.test_code, specimen_type: i.specimen_type, result_value: i.result_value, result_unit: i.result_unit, normal_range: i.normal_range, result_flag: i.result_flag || 'Normal' }))
                                            );
                                        }}
                                        style={{ padding: '5px 14px', background: ready === 0 ? '#94a3b8' : '#0369a1', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: ready === 0 ? 'default' : 'pointer' }}>
                                        🖨️ Print {ready > 0 ? `(${ready}/${total})` : ''}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <PatientLookup ref={patientLookupRef} onPatientReady={setPatient} department="Laboratory" hideButton onAfterRegister={createOrder}
                    onReprint={lastOrder ? (p) => printLabOrderSlip(p, lastOrder.orderNumber, lastOrder.tests, lastOrder.form) : null} />

                <div style={{ background: 'white', borderRadius: 10, padding: 24, marginBottom: 16, border: '1px solid #e2e8f0' }}>
                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', marginBottom: 12 }}>Order Details</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Source</label>
                            <SearchableSelect value={form.source} onChange={v => setForm(f => ({ ...f, source: v }))} options={SOURCE_OPTIONS} placeholder="Search source..." />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Priority</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                                <option>STAT</option><option>Urgent</option><option>Routine</option>
                            </select>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Clinical Information</label>
                            <textarea style={{ ...inputStyle, height: 60 }} value={form.clinical_info} onChange={e => setForm(f => ({ ...f, clinical_info: e.target.value }))} placeholder="Diagnosis, clinical notes..." />
                        </div>
                    </div>
                </div>

                {/* Search Box */}
                <div style={{ background: 'white', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #e2e8f0' }}>
                    <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16 }}>🔍</span>
                        <input
                            style={{ ...inputStyle, paddingLeft: 36, fontSize: 13 }}
                            placeholder="Search test by name or code (e.g. CBC, Blood Sugar, Thyroid...)"
                            value={searchQ}
                            onChange={e => setSearchQ(e.target.value)}
                        />
                    </div>
                    {searchResults.length > 0 && (
                        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                            {searchResults.map(t => (
                                <label key={t.id} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    gap: 10, padding: '8px 12px',
                                    border: `1px solid ${selectedTests.includes(t.id) ? '#7c3aed' : '#e2e8f0'}`,
                                    borderRadius: 7, cursor: 'pointer',
                                    background: selectedTests.includes(t.id) ? '#f5f3ff' : 'white'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <input type="checkbox" checked={selectedTests.includes(t.id)} onChange={() => toggleTest(t.id)}
                                            style={{ accentColor: '#7c3aed', width: 14, height: 14 }} />
                                        <div>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{t.test_name}</div>
                                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{t.test_code} | {t.specimen_type}</div>
                                        </div>
                                    </div>
                                    {t.price > 0 && (
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', whiteSpace: 'nowrap' }}>Rs. {t.price}</span>
                                    )}
                                </label>
                            ))}
                        </div>
                    )}
                    {searchQ.trim().length > 1 && searchResults.length === 0 && (
                        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, marginBottom: 0 }}>No tests found for "{searchQ}"</p>
                    )}
                </div>

                {/* Test Selection Tabs */}
                <div style={{ background: 'white', borderRadius: 10, padding: 24, marginBottom: 16, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', margin: 0 }}>
                            Select Tests {selectedTests.length > 0 && <span style={{ background: '#7c3aed', color: 'white', borderRadius: 12, padding: '2px 10px', fontSize: 11, marginLeft: 8 }}>{selectedTests.length} selected</span>}
                        </h3>
                        <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
                            {['profiles','categories'].map(t => (
                                <button key={t} type="button" onClick={() => setTestTab(t)}
                                    style={{ padding: '5px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                                        background: testTab === t ? '#7c3aed' : 'transparent',
                                        color: testTab === t ? 'white' : '#64748b' }}>
                                    {t === 'profiles' ? '📦 Profiles' : '📋 By Category'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Profiles Tab */}
                    {testTab === 'profiles' && (
                        <div>
                            {profiles.length === 0 && <p style={{ fontSize: 12, color: '#94a3b8' }}>No profiles available</p>}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                                {profiles.map(prof => {
                                    const profTestIds = (prof.test_details || []).map(t => t.id);
                                    const allSelected = profTestIds.length > 0 && profTestIds.every(id => selectedTests.includes(id));
                                    const someSelected = profTestIds.some(id => selectedTests.includes(id));
                                    return (
                                        <div key={prof.id} style={{ border: `2px solid ${allSelected ? '#7c3aed' : someSelected ? '#c4b5fd' : '#e2e8f0'}`, borderRadius: 10, padding: 14,
                                            background: allSelected ? '#f5f3ff' : 'white', cursor: 'pointer' }}
                                            onClick={() => {
                                                if (allSelected) {
                                                    setSelectedTests(prev => prev.filter(id => !profTestIds.includes(id)));
                                                } else {
                                                    setSelectedTests(prev => [...new Set([...prev, ...profTestIds])]);
                                                }
                                            }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', marginBottom: 4 }}>{prof.profile_name}</div>
                                            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 8 }}>{prof.profile_code} • {profTestIds.length} tests</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                {(prof.test_details || []).slice(0, 5).map(t => (
                                                    <span key={t.id} style={{ fontSize: 10, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#374151' }}>{t.test_name}</span>
                                                ))}
                                                {(prof.test_details || []).length > 5 && (
                                                    <span style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700 }}>+{prof.test_details.length - 5} more</span>
                                                )}
                                            </div>
                                            {allSelected && <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>✓ All selected — click to deselect</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Categories Tab */}
                    {testTab === 'categories' && (
                        <div>
                    <p style={{ fontSize: 11, color: '#64748b', marginBottom: 14 }}>Click on a category to expand tests</p>
                    {CATEGORIES.map(cat => {
                        const catTests = allTests.filter(t => t.category === cat);
                        const selectedInCat = catTests.filter(t => selectedTests.includes(t.id)).length;
                        const isOpen = openCategory === cat;
                        return (
                            <div key={cat} style={{ marginBottom: 8, border: `1px solid ${isOpen ? '#7c3aed' : '#e2e8f0'}`, borderRadius: 8, overflow: 'hidden' }}>
                                <button type="button" onClick={() => toggleCategory(cat)}
                                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '11px 16px', background: isOpen ? '#f5f3ff' : '#f8fafc',
                                        border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: isOpen ? '#7c3aed' : '#1e3a5f' }}>{cat}</span>
                                        <span style={{ fontSize: 11, color: '#64748b' }}>({catTests.length} tests)</span>
                                        {selectedInCat > 0 && (
                                            <span style={{ background: '#7c3aed', color: 'white', borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>
                                                {selectedInCat} ✓
                                            </span>
                                        )}
                                    </div>
                                    <span style={{ fontSize: 16, color: '#7c3aed' }}>{isOpen ? '▲' : '▼'}</span>
                                </button>
                                {isOpen && (
                                    <div style={{ padding: '12px 16px', background: 'white', borderTop: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                                            {catTests.map(t => (
                                                <label key={t.id} style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    gap: 10, padding: '8px 12px',
                                                    border: `1px solid ${selectedTests.includes(t.id) ? '#7c3aed' : '#e2e8f0'}`,
                                                    borderRadius: 7, cursor: 'pointer',
                                                    background: selectedTests.includes(t.id) ? '#f5f3ff' : 'white'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <input type="checkbox" checked={selectedTests.includes(t.id)} onChange={() => toggleTest(t.id)}
                                                            style={{ accentColor: '#7c3aed', width: 14, height: 14 }} />
                                                        <div>
                                                            <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{t.test_name}</div>
                                                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{t.test_code} | {t.specimen_type}</div>
                                                        </div>
                                                    </div>
                                                    {t.price > 0 && (
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', whiteSpace: 'nowrap' }}>Rs. {t.price}</span>
                                                    )}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {allTests.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 24, color: '#64748b', fontSize: 13 }}>
                            No tests found — please run the seedLabTests.js script
                        </div>
                    )}
                        </div>
                    )}
                </div>

                {/* Billing Summary */}
                {selectedTests.length > 0 && (
                    <div style={{ background: 'white', borderRadius: 10, padding: 24, marginBottom: 16, border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', marginBottom: 16 }}>Billing Summary</h3>

                        {/* Selected tests list */}
                        <div style={{ marginBottom: 16 }}>
                            {selectedTestObjects.map(t => (
                                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                                    <span style={{ color: '#374151' }}>{t.test_name}</span>
                                    <span style={{ fontWeight: 600, color: '#1e3a5f' }}>Rs. {parseFloat(t.price) || 0}</span>
                                </div>
                            ))}
                        </div>

                        {/* Discount + Totals */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'end' }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4, color: '#374151' }}>Discount (%)</label>
                                <input
                                    type="number" min="0" max="100"
                                    style={{ ...inputStyle, width: 140 }}
                                    value={discount}
                                    onChange={e => setDiscount(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                                    Subtotal: <strong>Rs. {subtotal.toFixed(0)}</strong>
                                </div>
                                {discountAmt > 0 && (
                                    <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 4 }}>
                                        Discount ({discount}%): <strong>- Rs. {discountAmt.toFixed(0)}</strong>
                                    </div>
                                )}
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#059669', borderTop: '2px solid #e2e8f0', paddingTop: 8, marginTop: 4 }}>
                                    Net Total: Rs. {netTotal.toFixed(0)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <button type="button" disabled={loading}
                    onClick={() => patient ? createOrder(patient) : patientLookupRef.current?.triggerRegister()}
                    style={{ padding: '12px 32px', background: loading ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                    {loading ? 'Processing...' : patient ? '✅ Create Order' : '✅ Confirm Patient & Continue'}
                </button>
            </form>
        </div>
    );
}
